import { t } from "../i18n/index.ts";
import { spotTraceHasLocation } from "../../shared/route-trace.ts";
import { normalizeQuestSpotImages, type QuestSpot } from "./types.ts";
import { el } from "./dom.ts";
import { bindSpotCardPhotoLightbox } from "./spot-photo-lightbox.ts";
import { SpotLocationEmbed } from "./spot-location-embed.ts";
import type { LngLat } from "../../shared/types.ts";

export interface SpotPreviewBuildOpts {
  /** Register embed maps for teardown on preview rebuild. */
  onEmbed?: (embed: SpotLocationEmbed) => void;
  showTypeLabel?: boolean;
}

function spotCardThumbPlaceholder(): HTMLElement {
  const ph = el("span", "qp-spot-card-thumb qp-spot-card-thumb-ph");
  ph.setAttribute("aria-hidden", "true");
  return ph;
}

function appendSpotCardThumb(head: HTMLElement, thumbUrl: string): void {
  const thumb = el("img", "qp-spot-card-thumb qp-spot-card-thumb-loading") as HTMLImageElement;
  thumb.src = thumbUrl;
  thumb.alt = "";
  thumb.decoding = "async";
  thumb.addEventListener(
    "load",
    () => {
      thumb.classList.remove("qp-spot-card-thumb-loading");
    },
    { once: true },
  );
  thumb.addEventListener(
    "error",
    () => {
      thumb.replaceWith(spotCardThumbPlaceholder());
    },
    { once: true },
  );
  head.append(thumb);
}

function appendSpotGalleryImage(gallery: HTMLElement, item: { image: string; caption?: string | null }): void {
  const figure = el("figure", "qp-spot-image-figure");
  const img = el("img", "qp-spot-image") as HTMLImageElement;
  img.src = item.image;
  img.alt = item.caption?.trim() ?? "";
  img.decoding = "async";
  img.addEventListener(
    "error",
    () => {
      img.classList.add("is-missing");
      img.removeAttribute("src");
    },
    { once: true },
  );
  figure.append(img);
  const cap = item.caption?.trim();
  if (cap) figure.append(el("figcaption", "qp-spot-image-caption", cap));
  gallery.append(figure);
}

/** One spot unit for the live preview / public page (card with text, photos, map). */
export function buildSpotTracePreviewCard(spot: QuestSpot, opts: SpotPreviewBuildOpts = {}): HTMLElement {
  const card = el("div", "qp-spot-card");
  const head = el("div", "qp-spot-card-head");
  const thumbUrl = spot.thumbnail_image?.trim();
  if (thumbUrl) {
    appendSpotCardThumb(head, thumbUrl);
  }
  const text = el("span", "qp-spot-text");
  if (opts.showTypeLabel !== false) {
    text.append(el("span", "qp-spot-kind", t("rt.spots.typeLabel")));
  }
  const name = spot.title?.trim() || t("quests.untitled");
  text.append(el("span", "qp-spot-name", name));
  const desc = spot.label?.trim();
  if (desc) text.append(el("span", "qp-spot-meta", desc));
  head.append(text);
  card.append(head);

  const images = normalizeQuestSpotImages(spot.images);
  if (images.length) {
    const gallery = el("div", "qp-spot-images");
    for (const item of images) {
      appendSpotGalleryImage(gallery, item);
    }
    card.append(gallery);
  }

  if (spotTraceHasLocation(spot)) {
    const mapHost = el("div", "qp-spot-loc-wrap");
    card.append(mapHost);
    const embed = new SpotLocationEmbed(mapHost, [spot.lng, spot.lat] as LngLat);
    opts.onEmbed?.(embed);
    requestAnimationFrame(() => embed.resize());
  }

  bindSpotCardPhotoLightbox(
    card,
    images.map((i) => i.image),
    images.map((i) => i.caption ?? null),
    thumbUrl ?? null,
  );

  return card;
}

/** Wrap spot cards in a preview section (optional heading for multi-spot blocks). */
export function spotTracePreviewSection(spots: QuestSpot[], opts: SpotPreviewBuildOpts = {}): HTMLElement {
  const section = el("section", "qp-spots");
  if (spots.length > 1) section.append(el("h2", "qp-h2", t("rt.page.spots")));
  for (const spot of spots) {
    section.append(buildSpotTracePreviewCard(spot, opts));
  }
  return section;
}
